/**
 * NotificationService
 *
 * Handles push notifications using Firebase Cloud Messaging
 * Integrates with OpenIM chat for message notifications
 */

import { messaging, messagingReady } from '@/lib/firebase';
import { getToken } from 'firebase/messaging';

import { setToStorage, getFromStorage, removeFromStorage, showToast } from '@/lib/utils';
import { FirebaseConfig, OpenIMConfig } from '@/lib/config';

// ============================================
// Types
// ============================================

export interface NotificationPayload {
    title: string;
    body: string;
    icon?: string;
    badge?: string;
    data?: Record<string, unknown>;
    click_action?: string;
}

export interface NotificationPermissionStatus {
    granted: boolean;
    denied: boolean;
    default: boolean;
}

interface OpenIMApiResponse {
    errCode?: number;
    errMsg?: string;
    errDlt?: string;
    data?: unknown;
}

interface NativeOpenIMPushInfo {
    ok?: boolean;
    fcmToken?: string | null;
    pushToken?: string | null;
    deviceToken?: string | null;
    registrationToken?: string | null;
    platformID?: number | string | null;
    platformId?: number | string | null;
    platform?: string | null;
}

interface TokenRegistrationOptions {
    platformID?: number;
    notifyOnFailure?: boolean;
}

declare global {
    interface Window {
        registerNativeOpenIMPush?: (payload?: Record<string, unknown>) => Promise<unknown>;
        unregisterNativeOpenIMPush?: (payload?: Record<string, unknown>) => Promise<unknown>;
        getNativeOpenIMPushInfo?: () => Promise<NativeOpenIMPushInfo>;
    }
}

// ============================================
// Notification Service
// ============================================

export class NotificationService {
    private static instance: NotificationService | null = null;
    private fcmToken: string | null = null;
    private notificationAudio: HTMLAudioElement | null = null;
    private audioUnlocked = false; // true once play() has succeeded inside a user gesture
    private pendingSound = false;
    private userID: string | null = null;
    private openimToken: string | null = null;
    private recentNotifiedMessages = new Map<string, number>();
    private backendTokenRegistration: { key: string; promise: Promise<boolean> } | null = null;
    private nativePushPlatformID: number | null = null;

    // Storage keys
    private static readonly FCM_TOKEN_KEY = 'fcm_token';
    private static readonly FCM_TOKEN_OWNER_KEY = 'fcm_token_owner';
    private static readonly NOTIFICATION_ENABLED_KEY = 'notification_enabled';
    private static readonly SW_ACTIVATION_TIMEOUT_MS = 10000;
    private static readonly MESSAGE_NOTIFICATION_DEDUP_WINDOW_MS = 30000;
    private static readonly MESSAGE_NOTIFICATION_MAX_CACHE_SIZE = 500;
    private static readonly FIREBASE_SW_SCRIPT_URL = '/firebase-messaging-sw.js';
    private static readonly FIREBASE_SW_SCOPE = '/firebase-cloud-messaging-push-scope';
    private static readonly LEGACY_FIREBASE_SW_SCRIPT_URLS = ['/static/firebase-messaging-sw.js'];
    private static readonly LEGACY_FIREBASE_SW_SCOPES = ['/static/'];

    static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    private constructor() {
        if (typeof window === 'undefined') return;

        // Capture phase fires before any React/Vue handler can call stopPropagation,
        // so this is guaranteed to run on the very first click anywhere on the page.
        document.addEventListener('click', () => {
            this.unlockAudio();
        }, { capture: true, passive: true });
    }

    private getNotificationTag(data?: Record<string, unknown>): string {
        const toIdentity = (value: unknown): string | undefined => {
            if (typeof value === 'string' && value.trim()) return value;
            if (typeof value === 'number' && Number.isFinite(value)) return String(value);
            return undefined;
        };

        const conversationID = toIdentity(data?.conversationID) || toIdentity(data?.groupID) || 'notification';
        const messageIdentity =
            toIdentity(data?.clientMsgID) ||
            toIdentity(data?.messageID) ||
            toIdentity(data?.seq) ||
            toIdentity(data?.serverMsgID) ||
            `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        return `chat-${conversationID}-${messageIdentity}`;
    }

    private shouldNotifyMessageOnce(key: string): boolean {
        const now = Date.now();

        this.recentNotifiedMessages.forEach((timestamp, cachedKey) => {
            if (now - timestamp > NotificationService.MESSAGE_NOTIFICATION_DEDUP_WINDOW_MS) {
                this.recentNotifiedMessages.delete(cachedKey);
            }
        });

        const lastNotifiedAt = this.recentNotifiedMessages.get(key);
        if (lastNotifiedAt && now - lastNotifiedAt < NotificationService.MESSAGE_NOTIFICATION_DEDUP_WINDOW_MS) {
            return false;
        }

        this.recentNotifiedMessages.set(key, now);

        if (this.recentNotifiedMessages.size > NotificationService.MESSAGE_NOTIFICATION_MAX_CACHE_SIZE) {
            const ordered = Array.from(this.recentNotifiedMessages.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, NotificationService.MESSAGE_NOTIFICATION_MAX_CACHE_SIZE);
            this.recentNotifiedMessages = new Map(ordered);
        }

        return true;
    }

    /**
     * Unlock the audio element for autoplay and play any pending sound.
     * Must be called from within a user gesture handler (onClick, onKeyDown, etc.).
     * Safe to call on every interaction — no-ops once already unlocked.
     */
    unlockAudio(): void {
        if (typeof window === 'undefined') return;
        if (this.isFlutterNativeApp()) {
            this.pendingSound = false;
            return;
        }

        if (!this.notificationAudio) {
            this.notificationAudio = new Audio('/notification.mp3');
            this.notificationAudio.load();
        }

        if (!this.audioUnlocked) {
            // We must call play() during the user gesture to unlock it
            this.notificationAudio.play()
                .then(() => {
                    this.audioUnlocked = true;

                    if (!this.pendingSound) {
                        // Pause immediately if we were just unlocking it without a pending notification
                        this.notificationAudio!.pause();
                        this.notificationAudio!.currentTime = 0;
                    } else {
                        // Let it finish playing since there's a pending notification
                        this.pendingSound = false;
                    }
                })
                .catch(() => {
                    // Silently ignore unlock failures
                });
        } else if (this.pendingSound) {
            this.pendingSound = false;
            this.playSound();
        }
    }

    /**
     * Initialize notification service
     * Called after user login
     */
    async initialize(userID: string, _imsdk: unknown, openimToken?: string): Promise<boolean> {
        try {
            console.log('🔔 Initializing NotificationService for user:', userID);

            this.userID = userID;
            this.openimToken = openimToken ?? null;

            // Pre-load browser audio for Web/PWA only. Native apps use iOS/Android
            // notification sound settings instead of WebView media playback.
            if (typeof window !== 'undefined' && !this.isFlutterNativeApp() && !this.notificationAudio) {
                this.notificationAudio = new Audio('/notification.mp3');
                this.notificationAudio.load();
            }

            if (this.isFlutterNativeApp()) {
                const registered = await this.registerNativeOpenIMPushToken();
                setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, registered);
                return registered;
            }

            // Check if browser supports notifications
            if (typeof window === 'undefined' || !('Notification' in window)) {
                console.warn('⚠️ Notifications not supported in this environment');
                return false;
            }

            // Wait for Firebase Messaging to finish its async isSupported() check.
            // Without this await, messaging is still null when the checks below run,
            // causing getAndSaveToken() to be skipped and no FCM token to be registered.
            await messagingReady;

            // Check current permission status
            const permission = await this.getPermissionStatus();

            if (permission.granted) {
                // Already granted, get token
                const token = messaging ? await this.getAndSaveToken() : null;
                const enabled = !messaging || !!token;
                setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, enabled);
                return enabled;
            } else if (permission.default) {
                // Auto-request permission
                const granted = await this.requestPermission();
                return granted;
            } else {
                // Denied
                console.log('⚠️ Notification permission denied');
                return false;
            }
        } catch (error) {
            console.error('❌ NotificationService initialization failed:', error);
            return false;
        }
    }

    /**
     * Request notification permission from user
     * Call this when user clicks "Enable Notifications" button
     */
    async requestPermission(): Promise<boolean> {
        try {
            console.log('🔔 Requesting notification permission...');

            if (this.isFlutterNativeApp()) {
                const registered = await this.registerNativeOpenIMPushToken();
                setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, registered);
                return registered;
            }

            const permission = await Notification.requestPermission();

            if (permission === 'granted') {
                console.log('✅ Notification permission granted');

                // Get and save FCM token if messaging is available
                const token = messaging ? await this.getAndSaveToken() : null;
                const enabled = !messaging || !!token;

                // Save preference
                setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, enabled);

                return enabled;
            } else {
                console.log('⚠️ Notification permission denied');
                setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, false);
                return false;
            }
        } catch (error) {
            console.error('❌ Failed to request notification permission:', error);
            return false;
        }
    }

    /**
     * Get current permission status
     */
    async getPermissionStatus(): Promise<NotificationPermissionStatus> {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            return { granted: false, denied: false, default: true };
        }

        const permission = Notification.permission;

        return {
            granted: permission === 'granted',
            denied: permission === 'denied',
            default: permission === 'default',
        };
    }

    /**
     * Get FCM token and save it
     */
    private async getAndSaveToken(): Promise<string | null> {
        try {
            if (!messaging) {
                console.warn('⚠️ Firebase Messaging not available');
                return null;
            }

            // Explicitly register/resolve an active SW registration before calling
            // getToken(). Without an active worker PushManager.subscribe can fail
            // with: "Subscription failed - no active Service Worker".
            const swRegistration = await this.resolveActiveFirebaseServiceWorker();
            if (!swRegistration) {
                console.warn('⚠️ No active Firebase Service Worker, skipping FCM token request');
                return null;
            }

            // Get FCM token
            const token = await getToken(messaging, {
                vapidKey: FirebaseConfig.VAPID_KEY,
                serviceWorkerRegistration: swRegistration,
            });

            if (token) {
                console.log('✅ FCM Token obtained:', token.substring(0, 20) + '...');
                this.fcmToken = token;

                // Save token to localStorage
                setToStorage(NotificationService.FCM_TOKEN_KEY, token);
                if (this.userID) {
                    setToStorage(NotificationService.FCM_TOKEN_OWNER_KEY, this.userID);
                }

                // Register token with OpenIM backend
                const registered = await this.registerTokenWithBackend(token);
                if (!registered) {
                    this.fcmToken = null;
                    removeFromStorage(NotificationService.FCM_TOKEN_KEY);
                    removeFromStorage(NotificationService.FCM_TOKEN_OWNER_KEY);
                    return null;
                }

                return token;
            } else {
                console.warn('⚠️ No FCM token available');
                return null;
            }
        } catch (error) {
            console.error('❌ Failed to get FCM token:', error);
            return null;
        }
    }

    private async resolveActiveFirebaseServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
        if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
            return undefined;
        }

        await this.unregisterLegacyFirebaseServiceWorkers();

        try {
            const registration = await navigator.serviceWorker.register(NotificationService.FIREBASE_SW_SCRIPT_URL, {
                scope: NotificationService.FIREBASE_SW_SCOPE,
                type: 'module',
                updateViaCache: 'none',
            });

            const activeRegistration = await this.waitForServiceWorkerActivation(registration);
            if (activeRegistration) {
                console.log('✅ Firebase Service Worker active:', {
                    scriptURL: NotificationService.FIREBASE_SW_SCRIPT_URL,
                    scope: activeRegistration.scope,
                });
                return activeRegistration;
            }

            console.warn('⚠️ Firebase SW registered but not active in time:', NotificationService.FIREBASE_SW_SCRIPT_URL);
        } catch (error) {
            console.warn(`⚠️ Failed to register Firebase SW at ${NotificationService.FIREBASE_SW_SCRIPT_URL}:`, error);
        }

        return undefined;
    }

    private normalizeServiceWorkerUrl(value: string): string | undefined {
        try {
            return new URL(value, window.location.origin).href.replace(/\/$/, '');
        } catch {
            return undefined;
        }
    }

    private getServiceWorkerScriptURL(registration: ServiceWorkerRegistration): string | undefined {
        return registration.active?.scriptURL ||
            registration.waiting?.scriptURL ||
            registration.installing?.scriptURL;
    }

    private isLegacyFirebaseServiceWorker(registration: ServiceWorkerRegistration): boolean {
        const scope = this.normalizeServiceWorkerUrl(registration.scope);
        const scriptURL = this.normalizeServiceWorkerUrl(this.getServiceWorkerScriptURL(registration) || '');
        const canonicalScope = this.normalizeServiceWorkerUrl(NotificationService.FIREBASE_SW_SCOPE);
        const legacyScopes = NotificationService.LEGACY_FIREBASE_SW_SCOPES
            .map(item => this.normalizeServiceWorkerUrl(item))
            .filter(Boolean);
        const legacyScriptURLs = NotificationService.LEGACY_FIREBASE_SW_SCRIPT_URLS
            .map(item => this.normalizeServiceWorkerUrl(item))
            .filter(Boolean);

        if (!scope || !scriptURL) return false;

        const isFirebaseWorker = scriptURL.endsWith('/firebase-messaging-sw.js');
        if (!isFirebaseWorker) return false;

        return scope !== canonicalScope ||
            legacyScopes.includes(scope) ||
            legacyScriptURLs.includes(scriptURL);
    }

    private async unregisterLegacyFirebaseServiceWorkers(): Promise<void> {
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map(async (registration) => {
                if (!this.isLegacyFirebaseServiceWorker(registration)) return;

                try {
                    const subscription = await registration.pushManager.getSubscription();
                    await subscription?.unsubscribe();
                } catch (error) {
                    console.warn('⚠️ Failed to unsubscribe legacy Firebase push subscription:', error);
                }

                const removed = await registration.unregister();
                if (removed) {
                    console.log('🧹 Removed legacy Firebase Service Worker:', registration.scope);
                }
            }));
        } catch (error) {
            console.warn('⚠️ Failed to inspect Firebase Service Worker registrations:', error);
        }
    }

    private waitForServiceWorkerActivation(
        registration: ServiceWorkerRegistration
    ): Promise<ServiceWorkerRegistration | undefined> {
        if (registration.active) return Promise.resolve(registration);

        return new Promise((resolve) => {
            const worker = registration.installing || registration.waiting;
            if (!worker) {
                resolve(undefined);
                return;
            }

            let settled = false;
            const settle = (value: ServiceWorkerRegistration | undefined) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                worker.removeEventListener('statechange', onStateChange);
                resolve(value);
            };

            const timeoutId = window.setTimeout(() => {
                settle(registration.active ? registration : undefined);
            }, NotificationService.SW_ACTIVATION_TIMEOUT_MS);

            const onStateChange = () => {
                if (worker.state === 'activated') {
                    settle(registration);
                } else if (worker.state === 'redundant') {
                    settle(undefined);
                }
            };

            worker.addEventListener('statechange', onStateChange);
        });
    }

    private toIdentity(value: unknown): string | undefined {
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (typeof value === 'number' && Number.isFinite(value)) return String(value);
        return undefined;
    }

    private getGroupIDFromChatPath(value?: string): string | undefined {
        if (!value) return undefined;
        const match = value.match(/^\/chat\/([^/?#]+)/);
        if (!match) return undefined;

        try {
            const decoded = decodeURIComponent(match[1]);
            return decoded.startsWith('sg_') ? decoded.slice(3) : decoded;
        } catch {
            return match[1].startsWith('sg_') ? match[1].slice(3) : match[1];
        }
    }

    private getGroupIDFromNotificationData(data?: Record<string, unknown>): string | undefined {
        const explicitGroupID =
            this.toIdentity(data?.groupID) ||
            this.toIdentity(data?.sourceID) ||
            this.toIdentity(data?.customerID);
        if (explicitGroupID) return explicitGroupID;

        const conversationID = this.toIdentity(data?.conversationID);
        if (!conversationID) return undefined;

        return conversationID.startsWith('sg_') ? conversationID.slice(3) : conversationID;
    }

    private normalizeSameOriginPath(value?: string): string | undefined {
        if (!value || typeof window === 'undefined') return undefined;

        try {
            const url = new URL(value, window.location.origin);
            if (url.origin !== window.location.origin) return undefined;
            return `${url.pathname}${url.search}${url.hash}`;
        } catch {
            return value.startsWith('/') ? value : undefined;
        }
    }

    private toAbsoluteSameOriginUrl(path?: string): string {
        const fallbackPath = this.buildChatOpenPath();

        try {
            const url = new URL(path || fallbackPath, window.location.origin);
            if (url.origin !== window.location.origin) {
                return new URL(fallbackPath, window.location.origin).href;
            }
            return url.href;
        } catch {
            return new URL(fallbackPath, window.location.origin).href;
        }
    }

    private isHostAppDeployment(): boolean {
        if (typeof window === 'undefined' || typeof document === 'undefined') return false;

        return window.location.pathname.startsWith('/home') ||
            !!document.getElementById('chat-container') ||
            !!(window as unknown as { jfcChatWidgetInstance?: unknown }).jfcChatWidgetInstance;
    }

    private buildChatOpenPath(groupID?: string): string {
        if (!this.isHostAppDeployment()) {
            return groupID ? `/chat/${encodeURIComponent(groupID)}` : '/';
        }

        const params = new URLSearchParams();
        params.set('openChat', '1');
        if (groupID) {
            params.set('groupID', groupID);
        }
        return `/home?${params.toString()}`;
    }

    private resolveNotificationClickAction(payload: NotificationPayload): string {
        const data = payload.data || {};
        const rawClickAction =
            payload.click_action ||
            this.toIdentity(data.click_action) ||
            this.toIdentity(data.clickAction);
        const normalizedClickAction = this.normalizeSameOriginPath(rawClickAction);
        const groupID =
            this.getGroupIDFromNotificationData(data) ||
            this.getGroupIDFromChatPath(normalizedClickAction);
        const fallbackPath = this.buildChatOpenPath(groupID);

        if (!normalizedClickAction) return this.toAbsoluteSameOriginUrl(fallbackPath);
        if (this.isHostAppDeployment() && normalizedClickAction.startsWith('/chat')) {
            return this.toAbsoluteSameOriginUrl(fallbackPath);
        }
        return this.toAbsoluteSameOriginUrl(normalizedClickAction);
    }

    private openNotificationClickTarget(path: string): void {
        if (typeof window === 'undefined') return;

        window.focus();
        if (!path) return;

        const targetUrl = new URL(path, window.location.origin);
        if (targetUrl.origin !== window.location.origin) return;

        const currentPath = window.location.pathname + window.location.search + window.location.hash;
        const targetPath = targetUrl.pathname + targetUrl.search + targetUrl.hash;
        if (currentPath === targetPath) {
            return;
        }

        window.location.href = targetUrl.href;
    }

    /**
     * Get current FCM token
     */
    getToken(): string | null {
        if (this.isFlutterNativeApp()) {
            return null;
        }
        return this.fcmToken || getFromStorage<string>(NotificationService.FCM_TOKEN_KEY);
    }

    isUsingNativePushBridge(): boolean {
        return this.isFlutterNativeApp();
    }

    getOpenIMPlatformID(): number {
        return this.getNativeOpenIMPlatformIDFromRuntime() ?? OpenIMConfig.platformId;
    }

    async refreshTokenRegistration(): Promise<boolean> {
        if (this.isFlutterNativeApp()) {
            return this.registerNativeOpenIMPushToken();
        }

        const token = this.getToken();
        if (!token || token.trim().length === 0) {
            return false;
        }
        return this.registerTokenWithBackend(token);
    }

    /**
     * Register FCM token with the OpenIM server via REST API.
     * OpenIM stores the token keyed by (account, platformID) and uses it
     * to deliver push notifications when the user is offline.
     * Equivalent to what the SDK's updateFcmToken() would do internally.
     */
    async registerTokenWithBackend(token: string, options: TokenRegistrationOptions = {}): Promise<boolean> {
        const normalizedToken = typeof token === 'string' ? token.trim() : '';
        if (!normalizedToken) {
            console.log('ℹ️ Skipping OpenIM FCM registration: token is empty');
            return false;
        }

        const platformID = options.platformID ?? OpenIMConfig.platformId;
        const notifyOnFailure = options.notifyOnFailure ?? true;
        const registrationKey = this.getBackendTokenRegistrationKey(normalizedToken, platformID);

        // Do not persist registration state locally. The OpenIM token can be missing or
        // stale independently of this browser's storage, so each chat session must post
        // its current FCM token. This only deduplicates overlapping requests in memory.
        if (this.backendTokenRegistration?.key === registrationKey) {
            return this.backendTokenRegistration.promise;
        }

        const promise = this.registerTokenWithBackendOnce(
            normalizedToken,
            platformID,
            notifyOnFailure
        )
            .finally(() => {
                if (this.backendTokenRegistration?.key === registrationKey) {
                    this.backendTokenRegistration = null;
                }
            });
        this.backendTokenRegistration = { key: registrationKey, promise };
        return promise;
    }

    /**
     * Remove the current device token from OpenIM before logout/account switch.
     * OpenIM's updateFcmToken API uses an empty token to clear the stored mapping.
     */
    async unregisterTokenWithBackend(): Promise<boolean> {
        this.backendTokenRegistration = null;
        return this.updateBackendToken('', 'unregister', false, OpenIMConfig.platformId);
    }

    private getBackendTokenRegistrationKey(token: string, platformID: number): string {
        return `${this.userID || 'unknown'}:${platformID}:${token}`;
    }

    private async registerTokenWithBackendOnce(
        normalizedToken: string,
        platformID: number,
        notifyOnFailure: boolean
    ): Promise<boolean> {
        // Register directly so OpenIM replaces any stale token with the current token.
        // Clearing first would introduce an unnecessary gap in offline-push coverage.
        return this.updateBackendToken(normalizedToken, 'register', notifyOnFailure, platformID);
    }

    private createOperationID(): string {
        return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }

    private getOpenIMErrorDetail(result: OpenIMApiResponse | null): string {
        const detail = result?.errDlt?.trim() || result?.errMsg?.trim();
        return detail || 'OpenIM rejected the notification registration request.';
    }

    private async updateBackendToken(
        token: string,
        action: 'register' | 'unregister',
        notifyOnFailure: boolean,
        platformID: number
    ): Promise<boolean> {
        if (!this.userID || !this.openimToken) {
            console.warn(`⚠️ No userID or openimToken, cannot ${action} FCM token with OpenIM`);
            return false;
        }

        const normalizedToken = typeof token === 'string' ? token.trim() : '';
        if (action === 'register' && !normalizedToken) {
            // iOS/Safari can legitimately have notification permission but no FCM token
            // (unsupported context or not yet provisioned). Skip noisy backend calls/toasts.
            console.log('ℹ️ Skipping OpenIM FCM registration: token is empty');
            return false;
        }

        try {
            if (action === 'register') {
                console.log('📤 Registering FCM token with OpenIM:', normalizedToken.substring(0, 20) + '...');
            }

            const operationID = this.createOperationID();
            const response = await fetch(`${OpenIMConfig.apiAddr}/third/fcm_update_token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': this.openimToken,
                    'operationID': operationID,
                },
                body: JSON.stringify({
                    account: this.userID,
                    platformID,
                    fcmToken: action === 'register' ? normalizedToken : '',
                    expireTime: 0,
                }),
            });

            let result: OpenIMApiResponse | null = null;
            try {
                result = await response.json();
            } catch (parseError) {
                console.warn(`⚠️ OpenIM FCM token ${action} returned a non-JSON response:`, parseError);
            }

            if (!response.ok || result?.errCode !== 0) {
                const errorDetail = this.getOpenIMErrorDetail(result);
                const isEmptyTokenError = /fcm\s*token\s*is\s*empty/i.test(errorDetail);
                console.warn(`⚠️ OpenIM FCM token ${action} failed:`, {
                    httpStatus: response.status,
                    errCode: result?.errCode,
                    errMsg: result?.errMsg,
                    errDlt: result?.errDlt,
                    operationID,
                });
                if (isEmptyTokenError) {
                    // Some OpenIM deployments can return this even when the client is in a
                    // transient iOS token state. Treat as non-fatal and keep UI quiet.
                    console.log(`ℹ️ Ignoring OpenIM FCM token ${action} empty-token response`);
                    return false;
                }
                if (notifyOnFailure) {
                    showToast(`Failed to enable notifications: ${errorDetail}`, 'error');
                }
                return false;
            }

            console.log(`✅ FCM token ${action}ed with OpenIM successfully`);
            return true;
        } catch (error) {
            console.warn(`⚠️ OpenIM FCM token ${action} failed (non-critical):`, error);
            if (notifyOnFailure) {
                showToast('Failed to enable notifications due to a network error. Please try again.', 'error');
            }
            return false;
        }
    }

    private isFlutterNativeApp(): boolean {
        return typeof window !== 'undefined' && typeof window.registerNativeOpenIMPush === 'function';
    }

    private getNativeOpenIMPlatformIDFromRuntime(): number | null {
        if (!this.isFlutterNativeApp() || typeof window === 'undefined') {
            return null;
        }

        const explicitPlatformID = this.normalizeNativePlatformID(
            (window as Window & {
                __nativeOpenIMPlatformID?: number | string;
            }).__nativeOpenIMPlatformID
        );
        if (explicitPlatformID) {
            return explicitPlatformID;
        }

        try {
            const storedPlatformID = window.localStorage.getItem('native_openim_platform_id');
            const parsedPlatformID = this.normalizeNativePlatformID(storedPlatformID);
            if (parsedPlatformID) {
                return parsedPlatformID;
            }
        } catch {
            // Ignore storage access failures and fall back to the user agent.
        }

        const userAgent = window.navigator.userAgent || '';
        if (/android/i.test(userAgent)) {
            return 2;
        }
        if (/iphone|ipad|ipod/i.test(userAgent)) {
            return 1;
        }
        return null;
    }

    private normalizeNativePlatformID(value: unknown): number | null {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    }

    private getNativePushTokenFromInfo(info: NativeOpenIMPushInfo): string {
        const candidates = [
            info.fcmToken,
            info.pushToken,
            info.deviceToken,
            info.registrationToken,
        ];
        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim().length > 0) {
                return candidate.trim();
            }
        }
        return '';
    }

    private async getNativeOpenIMPushInfo(): Promise<NativeOpenIMPushInfo | null> {
        if (typeof window === 'undefined' || typeof window.getNativeOpenIMPushInfo !== 'function') {
            return null;
        }

        try {
            return await window.getNativeOpenIMPushInfo();
        } catch (error) {
            console.warn('⚠️ Failed to read native push info from Flutter bridge:', error);
            return null;
        }
    }

    private async registerNativeOpenIMPushToken(): Promise<boolean> {
        if (!this.userID || !this.openimToken) {
            console.warn('⚠️ No userID or openimToken, cannot register native FCM token with OpenIM');
            return false;
        }

        const info = await this.getNativeOpenIMPushInfo();
        if (!info?.ok) {
            console.warn('⚠️ Flutter native push bridge returned no usable push token');
            return false;
        }

        const nativeToken = this.getNativePushTokenFromInfo(info);
        const platformID =
            this.normalizeNativePlatformID(info.platformID ?? info.platformId) ||
            this.getNativeOpenIMPlatformIDFromRuntime();
        if (!nativeToken || !platformID) {
            console.warn('⚠️ Native push info is missing token or platformID');
            return false;
        }

        const registered = await this.registerTokenWithBackend(nativeToken, {
            platformID,
            notifyOnFailure: true,
        });
        if (registered) {
            this.nativePushPlatformID = platformID;
            this.fcmToken = null;
            removeFromStorage(NotificationService.FCM_TOKEN_KEY);
            removeFromStorage(NotificationService.FCM_TOKEN_OWNER_KEY);
            console.log(`✅ Native FCM token registered with OpenIM platformID=${platformID}`);
        }
        return registered;
    }

    private async unregisterNativeOpenIMPushToken(): Promise<boolean> {
        const platformID = this.nativePushPlatformID;
        if (!platformID) {
            return true;
        }

        this.backendTokenRegistration = null;
        const removed = await this.updateBackendToken('', 'unregister', false, platformID);
        this.nativePushPlatformID = null;
        return removed;
    }

    /**
     * Show browser notification
     */
    async showNotification(payload: NotificationPayload): Promise<void> {
        try {
            if (typeof window === 'undefined' || !('Notification' in window)) {
                console.warn('⚠️ Notifications not supported');
                return;
            }

            if (Notification.permission !== 'granted') {
                console.warn('⚠️ Notification permission not granted');
                return;
            }

            const clickAction = this.resolveNotificationClickAction(payload);
            const notificationData = {
                ...(payload.data || {}),
                host: window.location.origin,
                click_action: clickAction,
            };

            // Prefer ServiceWorkerRegistration.showNotification for consistent behavior
            // across hidden/background tabs and service worker handling.
            if ('serviceWorker' in navigator) {
                try {
                    const reg = await this.resolveActiveFirebaseServiceWorker();
                    if (!reg) {
                        throw new Error('Firebase Service Worker is not active');
                    }
                    await reg.showNotification(payload.title, {
                        body: payload.body,
                        icon: payload.icon || '/icon-192x192.png',
                        badge: '/badge-72x72.png',
                        tag: this.getNotificationTag(notificationData),
                        requireInteraction: false,
                        data: notificationData,
                    });
                    return;
                } catch {
                    // Fall through to Notification API
                }
            }

            // Fallback: use Notification API (works on desktop, not on hidden mobile tabs)
            const notification = new Notification(payload.title, {
                body: payload.body,
                icon: payload.icon,
                tag: this.getNotificationTag(notificationData),
                requireInteraction: false,
                data: notificationData,
            });

            notification.onclick = (event) => {
                event.preventDefault();
                notification.close();
                this.openNotificationClickTarget(clickAction);
            };
        } catch (error) {
            console.error('❌ Failed to show notification:', error);
        }
    }

    /**
     * Show local notification for new chat message
     * Call this when receiving a new message via OpenIM SDK
     */
    async notifyNewMessage(params: {
        senderName: string;
        message: string;
        conversationID: string;
        messageID?: string;
        avatar?: string;
    }): Promise<void> {
        try {
            const stableMessageID = params.messageID?.trim() || `${params.senderName}:${params.message}`;
            const dedupeKey = `${params.conversationID}:${stableMessageID}`;
            if (!this.shouldNotifyMessageOnce(dedupeKey)) {
                return;
            }

            await this.showNotification({
                title: params.senderName,
                body: params.message,
                icon: params.avatar,
                data: {
                    conversationID: params.conversationID,
                    groupID: params.conversationID,
                    type: 'chat_message',
                    clientMsgID: params.messageID,
                    messageID: params.messageID,
                    click_action: `/chat/${params.conversationID}`,
                },
                click_action: `/chat/${params.conversationID}`,
            });
        } catch (error) {
            console.error('❌ Failed to notify new message:', error);
        }
    }

    /**
     * Play notification sound
     */
    playSound(): void {
        try {
            if (typeof window === 'undefined') return;
            if (this.isFlutterNativeApp()) {
                this.pendingSound = false;
                return;
            }

            if (!this.notificationAudio) {
                this.notificationAudio = new Audio('/notification.mp3');
                this.notificationAudio.load();
            }

            this.notificationAudio.currentTime = 0;
            const playPromise = this.notificationAudio.play();

            if (playPromise !== undefined) {
                playPromise.then(() => {
                    this.audioUnlocked = true;
                }).catch(() => {
                    // Recreate audio element in case it entered a broken state
                    // (e.g., after page sleep/wake or tab backgrounding)
                    this.notificationAudio = new Audio('/notification.mp3');
                    this.notificationAudio.load();
                    this.pendingSound = true;
                });
            }
        } catch {
            this.notificationAudio = new Audio('/notification.mp3');
            this.notificationAudio.load();
            this.pendingSound = true;
        }
    }

    /**
     * Check if notifications are enabled
     */
    isEnabled(): boolean {
        return getFromStorage<boolean>(NotificationService.NOTIFICATION_ENABLED_KEY) ?? false;
    }

    /**
     * Disable notifications
     */
    disable(): void {
        setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, false);
        console.log('🔕 Notifications disabled');
    }

    /**
     * Enable notifications (if permission already granted)
     */
    async enable(): Promise<boolean> {
        if (this.isFlutterNativeApp()) {
            const enabled = await this.registerNativeOpenIMPushToken();
            setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, enabled);
            return enabled;
        }

        const permission = await this.getPermissionStatus();

        if (permission.granted) {
            const token = messaging ? await this.getAndSaveToken() : null;
            const enabled = !messaging || !!token;
            setToStorage(NotificationService.NOTIFICATION_ENABLED_KEY, enabled);
            if (enabled) {
                console.log('🔔 Notifications enabled');
            }
            return enabled;
        } else {
            console.warn('⚠️ Cannot enable notifications without permission');
            return false;
        }
    }

    /**
     * Cleanup on logout
     */
    async cleanup(): Promise<void> {
        if (this.isFlutterNativeApp()) {
            await this.unregisterNativeOpenIMPushToken();
        } else {
            await this.unregisterTokenWithBackend();
        }
        this.fcmToken = null;
        this.nativePushPlatformID = null;
        this.userID = null;
        this.openimToken = null;
        removeFromStorage(NotificationService.FCM_TOKEN_KEY);
        removeFromStorage(NotificationService.FCM_TOKEN_OWNER_KEY);
        console.log('🧹 NotificationService cleaned up');
    }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();
